import { type JwtPayload, jwtDecode } from "jwt-decode";

export type OktaTokenBearer = {
  okta_access_token?: string | null;
  impersonatedBy?: OktaTokenBearer | null;
};

/**
 * The Okta access token every authenticated call should present.
 *
 * While impersonating, the server withholds the subject's own token and the actor's token arrives
 * under `impersonatedBy`. Acting as the actor is what keeps profile-service able to validate the
 * impersonation; the subject's token would authenticate as the student directly, outside that check.
 */
export const resolveOktaAccessToken = (currentUser: OktaTokenBearer | null | undefined): string | undefined => {
  const token = currentUser?.impersonatedBy?.okta_access_token ?? currentUser?.okta_access_token;

  return typeof token === "string" && token.trim() !== "" ? token : undefined;
};

export const getJwtExp = (token: string | null | undefined): number | undefined => {
  try {
    if (typeof token !== "string") return undefined;

    const decoded: JwtPayload = jwtDecode(token);
    const exp = decoded.exp ?? 0;

    return Number.isFinite(exp) ? exp : undefined;
  } catch {
    return undefined;
  }
};

export const hasTokenExpired = (token: string | null | undefined, skewSeconds = 30): boolean => {
  const exp = getJwtExp(token);
  if (!exp) return true; // if it isn't a JWT / no exp, fall back to “presence”
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
};

export const shouldRefreshToken = (token: string | null | undefined, refreshWindowSeconds = 60): boolean => {
  const exp = getJwtExp(token);
  if (!exp) return false;
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + refreshWindowSeconds;
};
